import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, DOCUMENT, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { encrypt } from '@por/consumer-login/shared-lib';
import { Language } from '@por/shared/core';
import { User } from '../models';
import { ConsumerLoginConfig } from '../models/consumer-login-config';
import { LocalStorageService } from '../services/local-storage.service';
import { PrivacyPolicyService } from '../services/privacy-policy.service';

@Component({
    standalone: false,
    selector: 'por-signin',
    templateUrl: './signin-component.component.html',
    styleUrls: ['./signin-component.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SigninComponentComponent implements OnInit {
    private readonly fb = inject(FormBuilder);
    private readonly translateService = inject(TranslateService);
    private readonly privacyPolicyService = inject(PrivacyPolicyService);
    private readonly document = inject<Document>(DOCUMENT);
    private readonly localStorage = inject(LocalStorageService);

    @Output()
    readonly selectedTemplate: EventEmitter<string> = new EventEmitter<string>();

    @Output()
    readonly loginData: EventEmitter<User> = new EventEmitter<User>();

    inputs: ConsumerLoginConfig | undefined;
    @Input() config: string | undefined;
    @Input() isDisableButton = false;
    @Input() quickLinkUrl = '';
    loginForm!: FormGroup;

    async ngOnInit(): Promise<void> {
        this.translateService.use(Language.EN_GB);
        this.initializeForm();

        if (this.config && this.config.trim() !== '') {
            this.inputs = JSON.parse(this.config) as ConsumerLoginConfig;
        }

        this.setInitialFormValues();
    }

    initializeForm() {
        const formConfig: { [key: string]: unknown } = {
            username: ['', Validators.required],
            password: ['', Validators.required]
        };

        if (this.privacyPolicyService.isPrivacyTermsDisplayed()) {
            formConfig['privacyPolicyAccepted'] = [false, Validators.requiredTrue];
        }

        this.loginForm = this.fb.group(formConfig);
    }

    private setInitialFormValues(): void {
        const initialValues: { [key: string]: unknown } = {
            username: '',
            password: ''
        };

        if (this.privacyPolicyService.isPrivacyTermsDisplayed()) {
            initialValues['privacyPolicyAccepted'] = false;
        }

        this.loginForm.setValue(initialValues);
    }

    get username(): FormControl {
        return this.loginForm.get('username') as FormControl;
    }

    get password(): FormControl {
        return this.loginForm.get('password') as FormControl;
    }

    get privacyPolicyAccepted(): FormControl {
        return this.loginForm.get('privacyPolicyAccepted') as FormControl;
    }

    login(): boolean {
        const email: string = this.loginForm.value.username as string;
        const password: string = this.loginForm.value.password as string;

        if (!this.loginForm.valid) {
            return false;
        } else {
            this.localStorage.privacyPolicyAccepted = true;

            /* eslint-disable @typescript-eslint/naming-convention */
            /**
             * Note camelCase: DB model
             */
            this.loginData.emit({
                Email: email,
                Password: encrypt(password)
            });

            return true;
        }
    }

    loginCancel(): boolean {
        this.loginForm.patchValue({
            password: ''
        });

        return true;
    }

    setTemplate(templateName: string): void {
        this.selectedTemplate.emit(templateName);
    }

    onBackQL(): void {
        this.document.defaultView?.open(this.quickLinkUrl, '_self');
    }
}
